import React from "react";
import { cn } from "@/utils/cn";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      children,
      disabled = false,
      className,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

    const getVariantStyles = (variant: string) => {
      const customColor = "#473243";

      switch (variant) {
        case "primary":
          return {
            backgroundColor: customColor,
            color: "white",
            borderColor: customColor,
          };
        case "secondary":
          return "bg-secondary-600 text-white hover:bg-secondary-700 focus-visible:ring-secondary-500";
        case "outline":
          return {
            borderWidth: "2px",
            borderColor: customColor,
            color: customColor,
            backgroundColor: "transparent",
          };
        case "ghost":
          return {
            color: customColor,
            backgroundColor: "transparent",
          };
        default:
          return "";
      }
    };

    const variantStyle = getVariantStyles(variant);

    const sizeStyles = {
      sm: "px-theme-sm py-1.5 text-sm rounded-theme-sm",
      md: "px-theme-md py-2 text-base rounded-theme-md",
      lg: "px-theme-lg py-2.5 text-lg rounded-theme-lg",
    };

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (variant === "primary") {
        e.currentTarget.style.backgroundColor = "#5a3f54";
      } else if (variant === "outline") {
        e.currentTarget.style.backgroundColor = "#f5f2f4";
      } else if (variant === "ghost") {
        e.currentTarget.style.backgroundColor = "#f5f2f4";
      }
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (variant === "primary") {
        e.currentTarget.style.backgroundColor = "#473243";
      } else if (variant === "outline" || variant === "ghost") {
        e.currentTarget.style.backgroundColor = "transparent";
      }
    };

    return (
      <button
        ref={ref}
        className={cn(
          baseStyles,
          typeof variantStyle === "string" ? variantStyle : "",
          sizeStyles[size],
          className
        )}
        style={typeof variantStyle === "object" ? variantStyle : undefined}
        disabled={disabled}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
